import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import useIsMobile from '@/hooks/useIsMobile'
import { api } from '@/lib/api/client'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { theme } from '@/lib/theme'
import Button from './ui/Button'
import Container from './ui/Container'
import Input from './ui/Input'
import Message from './ui/Message'
import ImportChatGPT from './ImportChatGPT'
import { ProfileHelpProvider, ProfileHelpToggle, useProfileHelp } from './helpers/ProfileHelp'

const DESCRIPTION_CACHE_KEY = 'profile_description_cache'

interface CachedData {
  value: string
  timestamp?: number // kept for backwards compat, not used
}

function ProfileContent() {
  const { helpEnabled } = useProfileHelp()
  const { t } = useTranslation()
  const { colors } = useThemeMode()
  const { user, isLoading: isUserLoading } = useUser()
  const isMobile = useIsMobile()
  const [description, setDescription] = useState<string>('')
  const [updateMessage, setUpdateMessage] = useState<string>('')
  const [isDescriptionLoading, setIsDescriptionLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null)
  const [descriptionError, setDescriptionError] = useState<string | null>(null)
  const [descriptionRemaining, setDescriptionRemaining] = useState<number | null>(null)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [isSharing, setIsSharing] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showImportChatGPTModal, setShowImportChatGPTModal] = useState(false)
  const [copyFeedback, setCopyFeedback] = useState(false)

  const descriptionFetchInProgress = useRef(false)

  const generateDescription = useCallback(async () => {
    if (descriptionFetchInProgress.current) return
    descriptionFetchInProgress.current = true
    try {
      setIsDescriptionLoading(true)
      setDescriptionError(null)
      const { data, error, status } = await api.api.profile.description.get()

      if (status === 429) {
        setDescriptionError(t('profile.description.rateLimitError'))
        setDescriptionRemaining(0)
        return
      }

      if (error) throw new Error('Failed to generate description')

      setDescription(data?.description || '')
      setDescriptionRemaining(data?.remaining ?? null)
      // Cache in localStorage with timestamp
      const cachedData: CachedData = {
        value: data?.description || '',
        timestamp: Date.now(),
      }
      localStorage.setItem(DESCRIPTION_CACHE_KEY, JSON.stringify(cachedData))
    } catch (err) {
      console.error('Error generating description:', err)
      setDescriptionError(t('profile.description.error'))
    } finally {
      setIsDescriptionLoading(false)
      descriptionFetchInProgress.current = false
    }
  }, [t])

  useEffect(() => {
    // Don't fetch data if user is not authenticated
    if (!user) {
      return
    }

    // Load description from cache
    const cachedDescriptionRaw = localStorage.getItem(DESCRIPTION_CACHE_KEY)

    if (cachedDescriptionRaw) {
      try {
        const cached: CachedData = JSON.parse(cachedDescriptionRaw)
        setDescription(cached.value)
      } catch {
        // Invalid cache format, treat as missing
      }
    }
  }, [user])

  const handleUpdate = async () => {
    if (!updateMessage.trim()) {
      setFeedbackMessage(t('profile.update.emptyMessage'))
      setTimeout(() => setFeedbackMessage(null), 2000)
      return
    }

    setIsUpdating(true)
    setFeedbackMessage(null)

    localStorage.removeItem(DESCRIPTION_CACHE_KEY)

    try {
      // Submit the update
      const { error } = await api.api.profile.update.post({
          message: updateMessage,
        })

      if (error) throw new Error('Failed to update profile')

      // Clear input and state - user must regenerate manually
      setUpdateMessage('')
      setDescription('')

      setFeedbackMessage(t('profile.update.success'))
      setTimeout(() => setFeedbackMessage(null), 3000)
    } catch (err) {
      console.error('Error updating profile:', err)
      setFeedbackMessage(t('profile.update.error'))
      setTimeout(() => setFeedbackMessage(null), 3000)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isUpdating) {
      handleUpdate()
    }
  }

  const handleGenerateDescription = () => {
    generateDescription()
  }

  const handleShare = async () => {
    setIsSharing(true)
    setFeedbackMessage(null)

    try {
      const { data, error } = await api.api.profile.share.post({
          peerCard: '',
          description: description || '',
        })

      if (error) throw new Error('Failed to create shareable profile')

      setShareUrl(data?.url || '')
    } catch (err) {
      console.error('Error creating shareable profile:', err)
      setFeedbackMessage(t('profile.share.error'))
      setTimeout(() => setFeedbackMessage(null), 3000)
    } finally {
      setIsSharing(false)
    }
  }

  const handleCopyLink = async () => {
    if (!shareUrl) return

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
    } catch (err) {
      console.error('Error copying to clipboard:', err)
      setFeedbackMessage(t('profile.share.copyError'))
      setTimeout(() => setFeedbackMessage(null), 2000)
    }
  }

  // Show loading state while checking authentication
  if (isUserLoading) {
    return (
      <Container style={{ overflow: 'auto', padding: theme.spacing.lg }}>
        <Message variant="loading">{t('profile.loading')}</Message>
      </Container>
    )
  }

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <Container
        style={{
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: theme.spacing.lg,
          gap: theme.spacing.md,
        }}
      >
        <div style={{ fontSize: '32px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔐')}</div>
        <div
          style={{
            color: colors.primaryText,
            fontSize: isMobile ? '14px' : theme.fontSize.md,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {t('profile.authRequired')}
        </div>
        <div
          style={{
            color: colors.secondaryText,
            fontSize: isMobile ? '12px' : theme.fontSize.sm,
            textAlign: 'center',
            maxWidth: '280px',
          }}
        >
          {t('profile.authRequiredMessage')}
        </div>
      </Container>
    )
  }

  return (
    <Container style={{ overflow: 'auto', padding: isMobile ? theme.spacing.sm : theme.spacing.lg }}>
      {/* Header with Help Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.md,
          gap: theme.spacing.md,
        }}
      >
        <div style={{ display: 'flex', gap: theme.spacing.sm }}>
          <Button
            onClick={() => {
              setShowShareModal(true)
              if (!shareUrl) {
                handleShare()
              }
            }}
            variant="secondary"
            size="md"
            disabled={isSharing}
            isLoading={isSharing}
          >
            {t('profile.shareYourProfile')}
          </Button>
          <Button
            onClick={() => setShowImportChatGPTModal(true)}
            variant="secondary"
            size="md"
          >
            {t('menu.importFromChatGPT')}
          </Button>
        </div>
        <ProfileHelpToggle />
      </div>

      {!isUpdating && feedbackMessage && (
        <Message
          variant={feedbackMessage.startsWith('Error:') ? 'error' : 'success'}
          style={{
            marginBottom: theme.spacing.md,
            fontSize: theme.fontSize.sm,
          }}
        >
          {feedbackMessage}
        </Message>
      )}

      {isUpdating && (
        <div style={{ marginBottom: theme.spacing.xl }}>
          <Message variant="loading">{t('profile.updatingProfile')}</Message>
        </div>
      )}

      {/* Profile Description */}
      {!isUpdating && (
        <section style={{ marginBottom: theme.spacing.lg }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.xs,
              marginBottom: theme.spacing.sm,
            }}
          >
            <h3
              style={{
                fontSize: isMobile ? '12px' : theme.fontSize.sm,
                fontWeight: 'bold',
                margin: 0,
                color: colors.primaryAccent,
              }}
            >
              {t('profile.description.title')}
            </h3>
          </div>
          {/* Description Help Banner */}
          {helpEnabled && (
            <div
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: colors.primaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: theme.borderRadius.sm,
                color: colors.secondaryText,
                fontSize: theme.fontSize.sm,
                lineHeight: '1.6',
                marginBottom: theme.spacing.sm,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.xs,
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  minWidth: '16px',
                  borderRadius: '50%',
                  backgroundColor: colors.primaryAccent,
                  color: colors.primaryBg,
                  fontSize: '10px',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  marginTop: '2px',
                  marginRight: '4px',
                }}
              >
                ?
              </span>
              <span>
                {t('help.profile.description')}
              </span>
            </div>
          )}
          {isDescriptionLoading ? (
            <div
              style={{
                padding: theme.spacing.md,
                backgroundColor: colors.secondaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: theme.borderRadius.md,
              }}
            >
              <Message variant="loading">
                {t('profile.description.loading')}
              </Message>
            </div>
          ) : descriptionError ? (
            <div
              style={{
                padding: theme.spacing.md,
                backgroundColor: colors.secondaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: theme.borderRadius.md,
              }}
            >
              <Message variant="error">{descriptionError}</Message>
            </div>
          ) : (
            <div
              style={{
                padding: isMobile ? theme.spacing.sm : theme.spacing.md,
                backgroundColor: colors.secondaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: isMobile ? '10px' : theme.borderRadius.md,
                color: colors.primaryText,
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                ...(isMobile ? {} : { maxHeight: '350px', overflow: 'auto' }),
                fontSize: isMobile ? '12px' : theme.fontSize.sm,
              }}
            >
              {description || t('profile.description.empty')}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.sm,
              marginTop: theme.spacing.sm,
            }}
          >
            <Button
              onClick={handleGenerateDescription}
              disabled={isDescriptionLoading || descriptionRemaining === 0}
              isLoading={isDescriptionLoading}
              variant="secondary"
              size="sm"
            >
              {t('profile.description.generate')}
            </Button>
            {descriptionRemaining !== null && (
              <span
                style={{
                  fontSize: theme.fontSize.xs,
                  color: descriptionRemaining === 0 ? colors.errorBorder : colors.secondaryText,
                }}
              >
                {t('profile.description.remaining', { count: descriptionRemaining })}
              </span>
            )}
          </div>
        </section>
      )}

      {/* Update Section */}
      <section style={{ marginBottom: theme.spacing.lg }}>
        <h3
          style={{
            fontSize: isMobile ? '12px' : theme.fontSize.sm,
            fontWeight: 'bold',
            marginBottom: theme.spacing.sm,
            color: colors.primaryAccent,
          }}
        >
          {t('profile.update.title')}
        </h3>
        <p
          style={{
            fontSize: isMobile ? '11px' : theme.fontSize.xs,
            color: colors.secondaryText,
            marginBottom: theme.spacing.sm,
          }}
        >
          {t('profile.update.subtitle')}
        </p>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing.sm,
            alignItems: 'flex-start',
          }}
        >
          <Input
            id="profile-update"
            value={updateMessage}
            onChange={(e) =>
              setUpdateMessage((e.target as HTMLInputElement).value)
            }
            onKeyUp={handleKeyPress}
            placeholder={t('profile.update.placeholder')}
            disabled={isUpdating}
            containerStyle={{ flex: 8, marginBottom: 0 }}
          />
          <Button
            onClick={handleUpdate}
            disabled={isUpdating}
            isLoading={isUpdating}
            style={{ flex: 1, marginBottom: 0 }}
          >
            {t('profile.update.button')}
          </Button>
        </div>
      </section>

      {/* Share Profile Modal */}
      {showShareModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '24px',
            fontFamily: "'Departure Mono', monospace",
          }}
          onClick={() => {
            setShowShareModal(false)
            setCopyFeedback(false)
          }}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              fontFamily: "'Departure Mono', monospace",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: colors.primaryText,
                marginBottom: '10px',
                fontFamily: "'Departure Mono', monospace",
              }}
            >
              {t('profile.share.title')}
            </h2>

            {isSharing ? (
              <Message variant="loading">{t('profile.share.generating')}</Message>
            ) : shareUrl ? (
              <>
                <p
                  style={{
                    fontSize: '12px',
                    color: colors.secondaryText,
                    marginBottom: '12px',
                    fontFamily: "'Departure Mono', monospace",
                  }}
                >
                  {t('profile.share.subtitle')}
                </p>
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      backgroundColor: colors.primaryBg,
                      border: `1px solid ${colors.primaryBorder}`,
                      borderRadius: '6px',
                      color: colors.primaryText,
                      fontSize: '11px',
                      fontFamily: "'Departure Mono', monospace",
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <Button
                    variant="primary"
                    onClick={handleCopyLink}
                    style={{ flex: 1 }}
                  >
                    {copyFeedback
                      ? t('profile.share.copied')
                      : t('profile.share.copy')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowShareModal(false)
                      setCopyFeedback(false)
                    }}
                    style={{ flex: 1 }}
                  >
                    {t('app.close')}
                  </Button>
                </div>
              </>
            ) : (
              <Message variant="error">{t('profile.share.error')}</Message>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Import from ChatGPT Modal */}
      {showImportChatGPTModal && (
        <ImportChatGPT
          onClose={() => setShowImportChatGPTModal(false)}
          onOpenSubscription={() => {
            setShowImportChatGPTModal(false)
            // Note: Profile doesn't have subscription modal, user would need to go through menu
          }}
        />
      )}
    </Container>
  )
}

export default function Profile() {
  return (
    <ProfileHelpProvider>
      <ProfileContent />
    </ProfileHelpProvider>
  )
}
